import clientSide from "@/helpers/clientSide";
import { useFetch } from "@/hooks/useFetch";
import { getCookieValue } from "@/lib/cookies";
import { trackCustomEvent } from "@/lib/newrelic";
import { buildTrackingContext } from "@/lib/tracking/apiTracking";
import { fetchSitecoreLayout } from "@/services/api/sitecoreLayout";
import { useEffect, useMemo } from "react";

export interface UseSitecoreLayoutState {
  layout: unknown | null;
  isLoading: boolean;
  error: Error | null;
  skipped: boolean;
  hasSCCookie: boolean;
  hasPSCCookie: boolean;
}

export function useSitecoreLayout(): UseSitecoreLayoutState {
  const hasSCCookie = getCookieValue("SC") !== null;
  const hasPSCCookie = getCookieValue("PSC") !== null;

  const shouldFetch = clientSide && !(hasSCCookie && hasPSCCookie);

  const trackingContext = useMemo(
    () =>
      buildTrackingContext({
        endpoint: "sitecoreLayout",
        hasSCCookie,
        hasPSCCookie,
      }),
    [hasSCCookie, hasPSCCookie]
  );

  const fetcher = useMemo(
    () => async (signal: AbortSignal) => {
      trackCustomEvent("sitecoreLayoutRequested", trackingContext);
      const result = await fetchSitecoreLayout({ signal });
      if (!result.error) {
        trackCustomEvent("sitecoreLayoutReturned", trackingContext);
      }
      return result;
    },
    [trackingContext]
  );

  const fetchOptions = useMemo(
    () => ({
      enabled: shouldFetch,
      trackingContext,
      errorSource: "sitecoreLayout",
      errorCustomAttributes: {
        sitecoreLayoutError: "Failed to fetch Sitecore layout",
      },
      // Sitecore CORS-blocks localhost when SSO cookies are absent; the failure
      // is expected during local dev. Errors are still sent to New Relic.
      quietOnLocalhost: true,
    }),
    [shouldFetch, trackingContext]
  );

  const { data, isLoading, error } = useFetch<unknown>(fetcher, fetchOptions);

  useEffect(() => {
    if (!shouldFetch) return;
    if (isLoading) return;

    trackCustomEvent("sitecoreLayoutCookiesTracked", {
      ...trackingContext,
      scCookie: getCookieValue("SC") || "",
      pscCookie: getCookieValue("PSC") || "",
    });
  }, [shouldFetch, isLoading, trackingContext]);

  return {
    layout: data,
    isLoading,
    error,
    skipped: !shouldFetch,
    hasSCCookie,
    hasPSCCookie,
  };
}
